/* ==========================================================================
   Custom section library for Dawn — behaviour
   Dependency-free. Safe to load once per page; re-running is idempotent.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- reveal */
  function initReveal(root) {
    var targets = (root || document).querySelectorAll('.cs-reveal:not([data-cs-revealed])');
    if (!targets.length) return;

    document.documentElement.classList.add('cs-reveal-ready');

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
        el.setAttribute('data-cs-revealed', '');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    targets.forEach(function (el, i) {
      // Stagger children within the same section only.
      if (!el.style.getPropertyValue('--cs-index')) {
        el.style.setProperty('--cs-index', String(i % 6));
      }
      el.setAttribute('data-cs-revealed', '');
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------- carousel */
  function initCarousels(root) {
    var nodes = (root || document).querySelectorAll('[data-cs-carousel]:not([data-cs-ready])');

    nodes.forEach(function (el) {
      el.setAttribute('data-cs-ready', '');

      var track = el.querySelector('.cs-carousel__track');
      var dots = el.querySelectorAll('.cs-carousel__dot');
      var prev = el.querySelector('[data-cs-prev]');
      var next = el.querySelector('[data-cs-next]');
      if (!track) return;

      var slides = Array.prototype.slice.call(track.children);

      function currentIndex() {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        slides.forEach(function (s, i) {
          var c = s.offsetLeft + s.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      }

      function scrollToIndex(i) {
        var s = slides[Math.max(0, Math.min(slides.length - 1, i))];
        if (!s) return;
        track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }

      function sync() {
        var i = currentIndex();
        dots.forEach(function (d, di) {
          d.setAttribute('aria-current', di === i ? 'true' : 'false');
        });
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) {
          next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        }
      }

      var raf;
      track.addEventListener('scroll', function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(sync);
      }, { passive: true });

      dots.forEach(function (d, i) {
        d.addEventListener('click', function () { scrollToIndex(i); });
      });
      if (prev) prev.addEventListener('click', function () { scrollToIndex(currentIndex() - 1); });
      if (next) next.addEventListener('click', function () { scrollToIndex(currentIndex() + 1); });

      window.addEventListener('resize', sync);
      sync();

      /* Optional autoplay for the hero */
      var interval = parseInt(el.getAttribute('data-cs-autoplay') || '0', 10);
      if (interval > 0 && slides.length > 1) {
        var timer = null;
        var start = function () {
          stop();
          timer = setInterval(function () {
            var i = currentIndex();
            scrollToIndex(i >= slides.length - 1 ? 0 : i + 1);
          }, interval);
        };
        var stop = function () { if (timer) { clearInterval(timer); timer = null; } };

        el.addEventListener('mouseenter', stop);
        el.addEventListener('mouseleave', start);
        el.addEventListener('focusin', stop);
        track.addEventListener('touchstart', stop, { passive: true });
        document.addEventListener('visibilitychange', function () {
          document.hidden ? stop() : start();
        });
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) start();
      }
    });
  }

  /* ------------------------------------------------------------ scroll-top */
  function initScrollTop(root) {
    var btn = (root || document).querySelector('[data-cs-scrolltop]:not([data-cs-ready])');
    if (!btn) return;
    btn.setAttribute('data-cs-ready', '');

    var threshold = parseInt(btn.getAttribute('data-threshold') || '600', 10);
    var onScroll = function () {
      btn.classList.toggle('is-visible', window.scrollY > threshold);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    onScroll();
  }

  /* ----------------------------------------------------------- promo popup */
  function initPromoPopup(root) {
    var pop = (root || document).querySelector('[data-cs-popup]:not([data-cs-ready])');
    if (!pop) return;
    pop.setAttribute('data-cs-ready', '');

    var key = 'cs-popup-' + (pop.getAttribute('data-key') || 'default');
    var delay = parseInt(pop.getAttribute('data-delay') || '5', 10) * 1000;
    var days = parseInt(pop.getAttribute('data-frequency') || '7', 10);
    var inEditor = window.Shopify && window.Shopify.designMode;

    function dismissed() {
      try {
        var until = parseInt(localStorage.getItem(key) || '0', 10);
        return Date.now() < until;
      } catch (e) { return false; }
    }
    function remember() {
      try { localStorage.setItem(key, String(Date.now() + days * 864e5)); } catch (e) {}
    }
    function open() {
      pop.classList.add('is-open');
      pop.removeAttribute('hidden');
      var closer = pop.querySelector('[data-cs-popup-close]');
      if (closer) closer.focus();
    }
    function close() {
      pop.classList.remove('is-open');
      pop.setAttribute('hidden', '');
      remember();
    }

    pop.querySelectorAll('[data-cs-popup-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    pop.addEventListener('click', function (e) {
      if (e.target === pop) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && pop.classList.contains('is-open')) close();
    });

    if (inEditor) { open(); return; }
    if (dismissed()) return;
    setTimeout(open, delay);
  }

  /* ------------------------------------------------------- comparison rows */
  function initStickyAtc(root) {
    var bar = (root || document).querySelector('[data-cs-sticky-atc]:not([data-cs-ready])');
    if (!bar) return;
    bar.setAttribute('data-cs-ready', '');

    var anchorSel = bar.getAttribute('data-anchor') || 'product-form';
    var anchor = document.querySelector('[id^="' + anchorSel + '"], .' + anchorSel);
    if (!anchor || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { rootMargin: '0px 0px -80% 0px' });
    io.observe(anchor);
  }

  /* ---------------------------------------------------------- before/after */
  function initBeforeAfter(root) {
    var figs = (root || document).querySelectorAll('[data-cs-beforeafter]:not([data-cs-ready])');

    figs.forEach(function (fig) {
      fig.setAttribute('data-cs-ready', '');

      var stage = fig.querySelector('.cs-ba__stage');
      var range = fig.querySelector('.cs-ba__range');
      if (!stage || !range) return;

      /* Flags the first interaction so the "drag me" hint and the nudging
         handle can retire once they've done their job. */
      var touched = false;
      var markTouched = function () {
        if (touched) return;
        touched = true;
        fig.setAttribute('data-cs-touched', '');
      };

      var apply = function () {
        stage.style.setProperty('--_pos', range.value + '%');
      };

      range.addEventListener('input', function () { markTouched(); apply(); });
      range.addEventListener('change', apply);
      range.addEventListener('focus', markTouched);

      /* A range input only tracks the thumb, so a click far from it jumps in
         steps. Map pointer position across the stage directly instead. */
      var dragging = false;
      var setFromPointer = function (clientX) {
        var r = stage.getBoundingClientRect();
        if (!r.width) return;
        var pct = ((clientX - r.left) / r.width) * 100;
        range.value = String(Math.max(0, Math.min(100, pct)));
        apply();
      };

      stage.addEventListener('pointerdown', function (e) {
        dragging = true;
        markTouched();
        stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
        setFromPointer(e.clientX);
      });
      stage.addEventListener('pointermove', function (e) {
        if (dragging) setFromPointer(e.clientX);
      });
      var stop = function () { dragging = false; };
      stage.addEventListener('pointerup', stop);
      stage.addEventListener('pointercancel', stop);
      stage.addEventListener('pointerleave', stop);

      apply();
    });
  }

  /* ------------------------------------------------------------- gallery */
  function initGallery(root) {
    var galleries = (root || document).querySelectorAll('[data-cs-gallery]:not([data-cs-ready])');

    galleries.forEach(function (gal) {
      gal.setAttribute('data-cs-ready', '');

      var main = gal.querySelector('[data-cs-gallery-main]');
      var thumbs = gal.querySelectorAll('[data-cs-gallery-thumb]');
      if (!main || !thumbs.length) return;

      function select(btn) {
        var full = btn.getAttribute('data-full');
        if (!full) return;

        /* Fade the swap so it doesn't pop. */
        main.style.opacity = '0';
        var swap = function () {
          main.src = full;
          var alt = btn.getAttribute('data-alt');
          if (alt) main.alt = alt;
          main.style.opacity = '1';
        };
        if (main.decode) {
          var pre = new Image();
          pre.src = full;
          pre.decode ? pre.decode().then(swap).catch(swap) : swap();
        } else {
          swap();
        }

        thumbs.forEach(function (t) {
          t.classList.toggle('is-active', t === btn);
          t.setAttribute('aria-selected', t === btn ? 'true' : 'false');
        });
      }

      thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () { select(btn); });
        btn.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var list = Array.prototype.slice.call(thumbs);
          var i = list.indexOf(btn);
          var next = list[e.key === 'ArrowRight' ? i + 1 : i - 1];
          if (next) { next.focus(); select(next); }
        });
      });
    });
  }

  /* --------------------------------------------------------------- likes */
  function initLikes(root) {
    var buttons = (root || document).querySelectorAll('[data-cs-like]:not([data-cs-ready])');

    buttons.forEach(function (btn) {
      btn.setAttribute('data-cs-ready', '');

      var key = 'cs-like-' + (btn.getAttribute('data-key') || '');
      var chip = document.querySelector('[data-cs-like-chip="' + btn.getAttribute('data-key') + '"]');
      var countEl = chip && chip.querySelector('[data-cs-like-count]');
      if (!countEl) return;

      var base = parseInt((countEl.textContent || '0').replace(/\D/g, ''), 10) || 0;

      function stored() {
        try { return localStorage.getItem(key) === '1'; } catch (e) { return false; }
      }
      function store(v) {
        try { v ? localStorage.setItem(key, '1') : localStorage.removeItem(key); } catch (e) {}
      }

      function render(liked) {
        var n = base + (liked ? 1 : 0);
        countEl.textContent = String(n);
        /* `hidden` rather than a class — a class-based display:none loses to
           the base rule on source order. */
        if (n === 0) {
          chip.setAttribute('hidden', '');
        } else {
          chip.removeAttribute('hidden');
        }
        btn.classList.toggle('is-liked', liked);
        btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
      }

      /* Remember across visits so a refresh doesn't inflate the count. */
      render(stored());

      btn.addEventListener('click', function () {
        var liked = !stored();
        store(liked);
        render(liked);

        if (liked) {
          chip.classList.remove('cs-rv__chip--pop');
          void chip.offsetWidth;           // restart the animation
          chip.classList.add('cs-rv__chip--pop');
        }
      });
    });
  }

  /* -------------------------------------------------------------- header */
  function initHeader(root) {
    var toggles = (root || document).querySelectorAll('[data-cs-hd-toggle]:not([data-cs-ready])');

    toggles.forEach(function (btn) {
      btn.setAttribute('data-cs-ready', '');
      var nav = document.getElementById(btn.getAttribute('aria-controls'));
      if (!nav) return;

      function setOpen(open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        nav.classList.toggle('is-open', open);
      }

      btn.addEventListener('click', function () {
        setOpen(btn.getAttribute('aria-expanded') !== 'true');
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') {
          setOpen(false);
          btn.focus();
        }
      });

      /* Close when tapping outside the menu. */
      document.addEventListener('click', function (e) {
        if (btn.getAttribute('aria-expanded') !== 'true') return;
        if (nav.contains(e.target) || btn.contains(e.target)) return;
        setOpen(false);
      });

      /* The nav is only a drawer on small screens; reset it on resize. */
      window.addEventListener('resize', function () {
        if (window.innerWidth >= 990) setOpen(false);
      });
    });
  }

  /* ------------------------------------------------- announcement rotation */
  function initAnnouncementRotate(root) {
    var bars = (root || document).querySelectorAll('[data-cs-ab-rotate]:not([data-cs-ready])');

    bars.forEach(function (el) {
      el.setAttribute('data-cs-ready', '');
      var slides = el.querySelectorAll('.cs-ab__slide');
      var wait = parseInt(el.getAttribute('data-cs-ab-rotate') || '0', 10);
      if (slides.length < 2 || !wait) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var i = 0;
      setInterval(function () {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, wait);
    });
  }

  /* -------------------------------------------------------------- bootstrap */
  function initAll(root) {
    initReveal(root);
    initCarousels(root);
    initScrollTop(root);
    initPromoPopup(root);
    initStickyAtc(root);
    initBeforeAfter(root);
    initAnnouncementRotate(root);
    initGallery(root);
    initHeader(root);
    initLikes(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: re-init a section when it is re-rendered */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });

  window.CustomSections = { init: initAll };
})();
